import { supabase } from '@/integrations/supabase/client';
import challengeService from './challengeService';
import { cleanText, decodeEntities } from '@/lib/text';

const transformArticle = (dbArticle: any) => ({
  id: dbArticle.id,
  title: cleanText(dbArticle.title),
  excerpt: cleanText(dbArticle.excerpt),
  content: decodeEntities(dbArticle.content),
  url: dbArticle.url,
  imageUrl: dbArticle.image_url,
  publishedAt: dbArticle.published_at,
  source: {
    id: dbArticle.source_id,
    name: cleanText(dbArticle.source_name),
    url: dbArticle.source_url,
    logoUrl: dbArticle.source_logo_url
  },
  author: dbArticle.author_name ? {
    name: cleanText(dbArticle.author_name),
    avatar: dbArticle.author_avatar
  } : undefined,
  category: cleanText(dbArticle.category) || 'General',
  readTime: dbArticle.read_time || '5 min read',
  likes_count: dbArticle.likes_count || 0,
  comments_count: dbArticle.comments_count || 0,
  audio_url: dbArticle.audio_url
});


export interface ArticleShare {
  id: string;
  article_id: string;
  sender_id: string;
  recipient_id: string;
  message: string | null;
  created_at: string;
  is_read: boolean;
  articles?: any;
  sender?: {
    id: string;
    username: string | null;
    full_name: string | null;
    avatar_url: string | null;
    avatar_background: string;
  };
}

export const articleShareService = {
  async shareArticle(articleId: string, recipientIds: string[], message?: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const shares = recipientIds.map(recipientId => ({
        article_id: articleId,
        sender_id: user.id,
        recipient_id: recipientId,
        message: message || null
      }));

      const { error } = await supabase
        .from('article_shares')
        .insert(shares);

      if (error) {
        console.error('Error sharing article:', error);
        return false;
      }

      // Update share_article challenge
      await challengeService.updateChallengeProgress('share_article', recipientIds.length);
      
      return true;
    } catch (error) {
      console.error('Error in shareArticle:', error);
      return false;
    }
  },

  async getSharedWithMe(): Promise<ArticleShare[]> {
    try {
      const { data, error } = await supabase
        .from('article_shares')
        .select(`
          *,
          articles(*)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching shares:', error);
        return [];
      }

      if (!data) return [];

      // Fetch sender profiles separately
      const senderIds = [...new Set(data.map(share => share.sender_id))];
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, username, full_name, avatar_url, avatar_background')
        .in('id', senderIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      return data.map(share => ({
        ...share,
        sender: profileMap.get(share.sender_id) || null,
        articles: share.articles ? transformArticle(share.articles) : null
      })) as ArticleShare[];
    } catch (error) {
      console.error('Error in getSharedWithMe:', error);
      return [];
    }
  },

  async markAsRead(shareId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('article_shares')
        .update({ is_read: true })
        .eq('id', shareId);

      if (error) {
        console.error('Error marking share as read:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in markAsRead:', error);
      return false;
    }
  }
};
